import { useState } from 'react';
import { usePracticeSearch } from '../../hooks/usePracticeSearch';
import { usePracticeOptions } from '../../hooks/useBilling';
import { SearchPickerField, SearchPickerList } from './SearchPicker';

export interface PracticeRow {
    id: string;
    name: string | null;
}

interface PracticeMultiSelectProps {
    selected: PracticeRow[];
    onChange: (next: PracticeRow[]) => void;
    /** Rows become clickable (navigate to the practice's own page) when set.
     *  Absent for filter-style callers where rows are inert. */
    onNavigate?: (practiceId: string) => void;
    placeholder: string;
    /** Shown in the selected-list panel when nothing is selected. */
    emptyText: string;
}

export function PracticeMultiSelect({
    selected,
    onChange,
    onNavigate,
    placeholder,
    emptyText
}: PracticeMultiSelectProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const { results, isLoading } = usePracticeSearch(search, open);
    // Only the selected rows' doctor lines read this list, so fetch it once the menu opens or a row exists.
    const { practices: practiceOptions } = usePracticeOptions(open || selected.length > 0);

    const doctorByPracticeId = new Map(
        practiceOptions.filter((p) => p.doctorName).map((p) => [p.id, p.doctorName as string])
    );

    const selectedIds = new Set(selected.map((p) => p.id));
    const matches = results.filter((p) => !selectedIds.has(p.id));

    const closeSearch = () => {
        setOpen(false);
        setSearch('');
    };

    const handlePick = (practice: PracticeRow) => {
        onChange([...selected, practice]);
        closeSearch();
    };

    const handleRemove = (id: string) => {
        onChange(selected.filter((p) => p.id !== id));
    };

    const status = isLoading
        ? { text: 'Searching…' }
        : matches.length === 0
          ? { text: 'No practices found.' }
          : null;

    return (
        <div className="flex flex-col gap-2">
            <SearchPickerField
                search={search}
                onSearchChange={setSearch}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={closeSearch}
                placeholder={placeholder}
                status={status}
                options={matches.map((p) => ({
                    key: p.id,
                    title: p.name,
                    subline: p.doctorName ? `Dr. ${p.doctorName}` : null,
                    onPick: () => handlePick({ id: p.id, name: p.name })
                }))}
            />
            <SearchPickerList
                emptyText={emptyText}
                rows={selected.map((practice) => {
                    const doctorName = doctorByPracticeId.get(practice.id);
                    const label = practice.name ?? practice.id;
                    return {
                        key: practice.id,
                        title: label,
                        titleMuted: !practice.name,
                        subline: doctorName ? `Dr. ${doctorName}` : null,
                        onNavigate: onNavigate ? () => onNavigate(practice.id) : undefined,
                        onRemove: () => handleRemove(practice.id),
                        removeLabel: onNavigate ? `Unlink ${label}` : `Remove ${label} filter`
                    };
                })}
            />
        </div>
    );
}
